import { getUrl } from '@/utils/getUrl';
import { isClient } from '@/utils/isClient';
import { isDev } from '@/utils/isDev';
import { isVideoPage } from '@/utils/isVideoPage';
import { logger } from '@/utils/logger';
import { tryGetWindowProp } from '@/utils/tryGetWindowProp';
import qs from 'query-string';
import { JW_CDN_URL } from '../constants';
import { ArticleInfo } from '../types/articleInfo';
import { replaceTag } from '../utils/replaceTag';

const addArticleDataToAdTag = ({
  tag,
  articleInfo,
  videoData,
  experimentGroup,
  adsPpId,
  isVerticalVideo,
  hasScrollArticles,
  posVideo,
  isPrebidAdtag
}: {
  tag: string;
  articleInfo?: ArticleInfo;
  videoData: any;
  experimentGroup: any;
  adsPpId: any;
  isVerticalVideo?: boolean;
  posVideo: string;
  isPrebidAdtag: boolean;
  hasScrollArticles?: boolean;
  explicitContent?: boolean;
}) => {
  const { articleVerticals = [], explicitContent = false } = articleInfo ?? {};
  const { wasYouplay } = videoData;
  const { url, query: tagQuery } = qs.parseUrl(tag);

  const custParams = tagQuery.cust_params || '';
  const [params] = Array.isArray(custParams) ? custParams : [custParams];

  const custParamsObj = qs.parse(params as string);
  // Add 'verticals'
  if (articleVerticals?.length) {
    // Put verticals in a set to create a list with unique values
    const verticalsSet = new Set([articleVerticals]);
    const verticals = Array.from(verticalsSet);

    custParamsObj['Vertikal'] = verticals.join(',');
  }

  if (experimentGroup) {
    custParamsObj['abgroup'] = experimentGroup;
  }
  // Add converted to youplay measurement
  if (wasYouplay) {
    // maybe have youplay ID here?

    custParamsObj['videoplayer'] = 'youplay';
  }

  custParamsObj['demandmanager'] = '1';
  if (isVerticalVideo) {
    custParamsObj['page'] = 'floatingvideo';
  }
  if (hasScrollArticles) {
    custParamsObj['secondarticle'] = 'true';
  }
  if (explicitContent) {
    custParamsObj['explicit'] = 'true';
  }
  if (isPrebidAdtag) {
    custParamsObj['pos'] = posVideo;
  } 

  return adsPpId
    ? `${url}?${qs.stringify({
        ...tagQuery,
        cust_params: qs.stringify(custParamsObj),
        ppid: adsPpId,
      })}`
    : `${url}?${qs.stringify({
        ...tagQuery,
        cust_params: qs.stringify(custParamsObj),
      })}`;
};

type WindowAds = {
  getAdScheduleId: () => any;
  getVideoExperimentGroup: () => any;
  getVideoPpId: () => string;
};

export const adsSetup = async ({
  articleInfo,
  isVerticalVideo,
  autoStart,
  wasYouplay,
  hasScrollArticles,
}: {
  articleInfo?: ArticleInfo;
  isVerticalVideo?: boolean;
  autoStart: string | boolean;
  wasYouplay: boolean;
  hasScrollArticles?: boolean;
}) => {
  let ads = null;
  ads = await tryGetWindowProp<WindowAds, undefined>('amav', { fallbackValue: undefined });

  if (!ads) {
    logger.warn(`adsSetup: window.ads not initialized`);
  }

  let adsScheduleId: any;
  let posVideo: string;
  const isVideo = isVideoPage();

  if (isDev) {
    adsScheduleId = process.env.OCELOT_ADS_SCHEDULE_ID;
  } else {
 
    try {
      const isViewable = autoStart === 'viewable';

      const startType = isViewable ? 'autoPlay' : 'clickToPlay';
      const pageType = isVideo ? 'videopage' : 'article';
      posVideo = isVideo ? 'videopage' : isViewable ? 'auto' : 'click';

      if (isClient() && ads) {
        const getAdScheduleId = window.amav?.getAdScheduleId;

        adsScheduleId = await getAdScheduleId?.(startType, pageType).catch((reason: string) => logger.debug(reason));
      }
    } catch (error: any) {
      logger.warn('Error thrown by getAdScheduleId', error);
    }
  }

  const response = await (await fetch(`${JW_CDN_URL}/advertising/schedules/${adsScheduleId}.json`)).json();

  const schedule = response?.schedule;

  if (!schedule) {
    throw new Error(`No ad schedule found in supplied ad config`);
  }

  let experimentGroup: any = null;

  try {
    if (isClient() && ads) {
      experimentGroup = await window.amav?.getVideoExperimentGroup().catch((reason: string) => logger.debug(reason));
    }
  } catch (e: any) {
    logger.warn('Error thrown by window.ads.getVideoExperimentGroup', e);
  }

  const adsPpId = isClient() ? ads?.getVideoPpId() : null;

  let adTagUrl: string | undefined;
  adTagUrl = await tryGetWindowProp<string, undefined>('amaTagUrl', { fallbackValue: undefined });

  if (!adTagUrl) {
    logger.warn(`adsSetup: window.adTagUrl not initialized`);
  }

  const modifyTag = () => {
    let dfpIdVal: any;

    const adTag = adTagUrl
      ? `${adTagUrl.replace('securepubads.g', 'pubads.g').replace('output=vast', 'output=xml_vmap1')}`
      : '';
    const isPrebidAdtag = adTagUrl ? true : false;

    if (isClient()) {
      const url = getUrl(window.location.href);
      dfpIdVal = url?.searchParams.get('dfp_id');
    }

    if (dfpIdVal) {
      // It is from a test article, show a test ad.
      return schedule.map(({ tag }: { tag: string }) => ({
        tag: replaceTag(adTag || tag, dfpIdVal),
      }));
    } else {
      return schedule.map(({ tag }: { tag: string }) => ({
        tag: addArticleDataToAdTag({
          tag: adTag || tag,
          articleInfo,
          videoData: { wasYouplay },
          experimentGroup,
          adsPpId,
          isVerticalVideo,
          hasScrollArticles,
          posVideo,
          isPrebidAdtag
        }),
      }));
    }
  };

  return {
    schedule: modifyTag(),
    adscheduleid: adsScheduleId,
    noConsent: false,
  };
};
